import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationCampusSelect,
  EducationGradeSelect,
  EducationSessionSelect,
} from '@/pages/education/components/EducationLookupSelects';

type ApiError = { response?: { data?: { message?: string } } };

type Application = Record<string, unknown> & {
  id: string;
  status: string;
  record_version: number;
  first_name?: string;
  last_name?: string | null;
  date_of_birth?: string | null;
  campus_id?: string;
  session_id?: string;
  grade_level_id?: string;
  campus_name?: string;
  session_name?: string;
  grade_name?: string;
  admission_no?: string | null;
};

type Detail = {
  application: Application;
  documents: Array<{ id: string; document_code: string; required_flag: number | boolean; status: string }>;
  assessments: Array<{ id: string; scheduled_at?: string; result_status: string; notes?: string }>;
  events: Array<{ id: string; from_status?: string; to_status: string; action_name: string; created_at?: string }>;
};

type Campus = { id: string; code: string; name: string };

const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say'];
const CATEGORIES = ['General', 'OBC', 'SC', 'ST', 'EWS', 'Other'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const MEDIUMS = ['English', 'Hindi', 'Bilingual', 'Other'];
const SOURCES = ['Walk-in', 'Phone enquiry', 'Website', 'Referral', 'Sibling', 'Staff', 'Alumni', 'Other'];
const RELATIONS = ['Father', 'Mother', 'Guardian', 'Other'];

type FormValues = {
  campus_id?: string;
  session_id?: string;
  grade_level_id?: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: dayjs.Dayjs | null;
  identity_number?: string;
  aadhaar_masked?: string;
  staff_child?: boolean;
  alumni_child?: boolean;
  [key: string]: unknown;
};

function asList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  return [];
}

function docLabel(code: string) {
  return code.replace(/_/g, ' ');
}

export default function AdmissionApplicationPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const back = `/education/admissions?${params.toString()}`;
  const [enquiryForm] = Form.useForm();
  const [appForm] = Form.useForm<FormValues>();
  const [campusForm] = Form.useForm();

  const campuses = useQuery({
    queryKey: ['education-lookup-campuses'],
    queryFn: async () => asList<Campus>((await api.get('/education/campuses')).data),
    enabled: !id,
  });

  const detail = useQuery({
    queryKey: ['education-admission', id],
    enabled: Boolean(id),
    queryFn: () => api.get<Detail>(`/education/admissions/${id}`),
  });

  const application = detail.data?.data?.application;
  const documents = detail.data?.data?.documents ?? [];

  useEffect(() => {
    if (!application || application.status !== 'draft') return;
    appForm.setFieldsValue({
      ...(application as FormValues),
      last_name: application.last_name || undefined,
      date_of_birth: application.date_of_birth ? dayjs(String(application.date_of_birth)) : undefined,
      staff_child: Boolean(application.staff_child),
      alumni_child: Boolean(application.alumni_child),
    });
  }, [application, appForm]);

  const createCampus = useMutation({
    mutationFn: (values: { code: string; name: string }) => api.post<Campus>('/education/campuses', values),
    onSuccess: (result) => {
      message.success(t('campusCreated', { defaultValue: 'Campus created' }));
      queryClient.invalidateQueries({ queryKey: ['education-lookup-campuses'] });
      if (result.data?.id) appForm.setFieldValue('campus_id', result.data.id);
      campusForm.resetFields();
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const create = useMutation({
    mutationFn: (values: FormValues) => {
      const payload = {
        ...values,
        date_of_birth: values.date_of_birth
          ? dayjs(values.date_of_birth as dayjs.Dayjs).format('YYYY-MM-DD')
          : undefined,
        identity_number: values.identity_number || values.aadhaar_masked,
        staff_child: Boolean(values.staff_child),
        alumni_child: Boolean(values.alumni_child),
      };
      return api.post<{ id: string }>('/education/admissions', payload);
    },
    onSuccess: (result) => {
      message.success(t('saved'));
      navigate(`/education/admissions/${result.data?.id}?${params.toString()}`);
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const saveDraft = useMutation({
    mutationFn: (values: FormValues) => {
      const payload = {
        ...values,
        date_of_birth: values.date_of_birth
          ? dayjs(values.date_of_birth as dayjs.Dayjs).format('YYYY-MM-DD')
          : undefined,
        identity_number: values.identity_number || values.aadhaar_masked,
        staff_child: Boolean(values.staff_child),
        alumni_child: Boolean(values.alumni_child),
        record_version: application?.record_version,
      };
      return api.put(`/education/admissions/${id}`, payload);
    },
    onSuccess: () => {
      message.success(t('saved'));
      queryClient.invalidateQueries({ queryKey: ['education-admission', id] });
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const enquiry = useMutation({
    mutationFn: (values: Record<string, string>) => api.post('/education/admissions/enquiries', values),
    onSuccess: () => {
      message.success(t('saved'));
      enquiryForm.resetFields();
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const verifyDoc = useMutation({
    mutationFn: (args: { documentId: string; status: string }) =>
      api.post(`/education/admissions/${id}/documents/${args.documentId}`, { status: args.status }),
    onSuccess: () => {
      message.success(t('saved'));
      queryClient.invalidateQueries({ queryKey: ['education-admission', id] });
    },
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const noCampus = !campuses.isLoading && (campuses.data?.length ?? 0) === 0;

  const applicationForm = (
    <Form
      form={appForm}
      layout="vertical"
      initialValues={{ nationality: 'Indian', medium_preference: 'English', source_channel: 'Walk-in' }}
      onFinish={(values) => (id ? saveDraft.mutate(values) : create.mutate(values))}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" title={t('admissionSeeking', { defaultValue: 'Admission seeking' })}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="campus_id" label={t('campus')} rules={[{ required: true }]}>
                <EducationCampusSelect />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="session_id" label={t('academicSession', { defaultValue: 'Academic session' })} rules={[{ required: true }]}>
                <EducationSessionSelect />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="grade_level_id" label={t('classApplied', { defaultValue: 'Class / grade applied for' })} rules={[{ required: true }]}>
                <EducationGradeSelect />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="medium_preference" label={t('mediumPreference', { defaultValue: 'Medium of instruction' })}>
                <Select options={MEDIUMS.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="source_channel" label={t('sourceChannel', { defaultValue: 'How did you hear about us?' })}>
                <Select options={SOURCES.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="staff_child" valuePropName="checked">
                <Checkbox>{t('staffChild', { defaultValue: 'Staff child' })}</Checkbox>
              </Form.Item>
              <Form.Item name="alumni_child" valuePropName="checked">
                <Checkbox>{t('alumniChild', { defaultValue: 'Alumni child' })}</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card size="small" title={t('childParticulars', { defaultValue: 'Child particulars' })}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="first_name" label={t('firstName')} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="last_name" label={t('lastName')}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="date_of_birth" label={t('dateOfBirth')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="gender" label={t('gender')}>
                <Select allowClear options={GENDERS.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="category" label={t('category', { defaultValue: 'Category' })}>
                <Select allowClear options={CATEGORIES.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="blood_group" label={t('bloodGroup', { defaultValue: 'Blood group' })}>
                <Select allowClear options={BLOOD_GROUPS.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="nationality" label={t('nationality', { defaultValue: 'Nationality' })}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="religion" label={t('religion', { defaultValue: 'Religion' })}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="mother_tongue" label={t('motherTongue', { defaultValue: 'Mother tongue' })}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="aadhaar_masked"
                label={t('aadhaar', { defaultValue: 'Aadhaar (last 4 / masked)' })}
                extra={t('aadhaarHint', { defaultValue: 'Used for office reference; full number can go in Identity number' })}
              >
                <Input maxLength={20} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="identity_number" label={t('identityNumber')}>
                <Input placeholder={t('aadhaarOrOtherId', { defaultValue: 'Aadhaar / other ID for de-dupe' })} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="sibling_details" label={t('siblingDetails', { defaultValue: 'Sibling in school (name / class)' })}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card size="small" title={t('previousSchooling', { defaultValue: 'Previous schooling' })}>
          <Row gutter={16}>
            <Col xs={24} md={10}>
              <Form.Item name="previous_school" label={t('previousSchool')}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={7}>
              <Form.Item name="previous_board" label={t('previousBoard', { defaultValue: 'Previous board' })}>
                <Input placeholder="CBSE / State / ICSE" />
              </Form.Item>
            </Col>
            <Col xs={24} md={7}>
              <Form.Item name="previous_class" label={t('previousClass', { defaultValue: 'Last class attended' })}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card size="small" title={t('parentGuardian', { defaultValue: 'Parents / guardian' })}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="father_name" label={t('fatherName', { defaultValue: 'Father name' })}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="father_phone" label={t('fatherPhone', { defaultValue: 'Father phone' })}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="father_occupation" label={t('fatherOccupation', { defaultValue: 'Father occupation' })}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="father_email" label={t('fatherEmail', { defaultValue: 'Father email' })}>
                <Input type="email" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="mother_name" label={t('motherName', { defaultValue: 'Mother name' })}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="mother_phone" label={t('motherPhone', { defaultValue: 'Mother phone' })}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="mother_occupation" label={t('motherOccupation', { defaultValue: 'Mother occupation' })}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="mother_email" label={t('motherEmail', { defaultValue: 'Mother email' })}>
                <Input type="email" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="guardian_name" label={t('guardianName')}>
                <Input placeholder={t('guardianIfDifferent', { defaultValue: 'If different from parents' })} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="guardian_relation" label={t('relationship')}>
                <Select allowClear options={RELATIONS.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="guardian_phone" label={t('guardianPhone', { defaultValue: 'Guardian phone' })}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="guardian_email" label={t('guardianEmail', { defaultValue: 'Guardian email' })}>
                <Input type="email" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card size="small" title={t('residentialAddress', { defaultValue: 'Residential address' })}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="address_line1" label={t('addressLine1', { defaultValue: 'Address line 1' })}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="address_line2" label={t('addressLine2', { defaultValue: 'Address line 2' })}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="city" label={t('city', { defaultValue: 'City / town' })}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="state_name" label={t('state', { defaultValue: 'State' })}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="pincode" label={t('pincode', { defaultValue: 'PIN code' })}>
                <Input maxLength={12} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="application_notes" label={t('notes', { defaultValue: 'Office notes' })}>
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Button type="primary" htmlType="submit" loading={create.isPending || saveDraft.isPending}>
          {id ? t('saveDraft', { defaultValue: 'Save draft' }) : t('save')}
        </Button>
      </Space>
    </Form>
  );

  return (
    <EducationPageChrome
      title={id ? t('application') : t('newApplication')}
      description={id ? undefined : t('admissionsCbseIntro', {
        defaultValue:
          'India CBSE private-school admission: capture child, parents, address, previous school, and document checklist. Fees and exams are out of scope for this release.',
      })}
      tags={application ? <Tag>{application.status}</Tag> : undefined}
      extra={<Button onClick={() => navigate(back)}>{t('backToAdmissions')}</Button>}
    >
      {id ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Card size="small" loading={detail.isLoading}>
            <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
              <Descriptions.Item label={t('applicant')}>
                {[application?.first_name, application?.last_name].filter(Boolean).join(' ') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('status')}>
                <Tag>{application?.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('campus')}>{String(application?.campus_name || application?.campus_code || '—')}</Descriptions.Item>
              <Descriptions.Item label={t('session')}>{String(application?.session_name || application?.session_code || '—')}</Descriptions.Item>
              <Descriptions.Item label={t('grade')}>{String(application?.grade_name || application?.grade_code || '—')}</Descriptions.Item>
              <Descriptions.Item label={t('admissionNumber')}>{String(application?.admission_no || '—')}</Descriptions.Item>
              <Descriptions.Item label={t('guardianName')}>{String(application?.guardian_name || '—')}</Descriptions.Item>
              <Descriptions.Item label={t('category', { defaultValue: 'Category' })}>
                {String(application?.category || '—')}
              </Descriptions.Item>
            </Descriptions>
            <Space wrap style={{ marginTop: 16 }}>
              <Button onClick={() => navigate(`/education/admissions/${id}/review?${params.toString()}`)}>{t('review')}</Button>
              <Button onClick={() => navigate(`/education/admissions/${id}/decision?${params.toString()}`)}>{t('decision')}</Button>
              <Button type="primary" onClick={() => navigate(`/education/admissions/${id}/offer?${params.toString()}`)}>{t('offer')}</Button>
            </Space>
          </Card>

          <Card size="small" title={t('documentChecklist', { defaultValue: 'CBSE document checklist' })}>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={documents}
              locale={{ emptyText: t('noDocuments', { defaultValue: 'No documents on this application' }) }}
              columns={[
                {
                  title: t('document', { defaultValue: 'Document' }),
                  dataIndex: 'document_code',
                  render: (code: string) => docLabel(code),
                },
                {
                  title: t('required', { defaultValue: 'Required' }),
                  dataIndex: 'required_flag',
                  width: 100,
                  render: (v: number | boolean) => (v ? <Tag color="orange">{t('required', { defaultValue: 'Required' })}</Tag> : '—'),
                },
                {
                  title: t('status'),
                  dataIndex: 'status',
                  width: 120,
                  render: (status: string) => (
                    <Tag color={status === 'verified' ? 'green' : status === 'rejected' ? 'red' : 'default'}>{status}</Tag>
                  ),
                },
                {
                  title: t('actions', { defaultValue: 'Actions' }),
                  width: 220,
                  render: (_v, row) => (
                    <Space>
                      <Button
                        size="small"
                        type="link"
                        onClick={() => verifyDoc.mutate({ documentId: row.id, status: 'verified' })}
                      >
                        {t('verify', { defaultValue: 'Verify' })}
                      </Button>
                      <Button
                        size="small"
                        type="link"
                        danger
                        onClick={() => verifyDoc.mutate({ documentId: row.id, status: 'rejected' })}
                      >
                        {t('reject', { defaultValue: 'Reject' })}
                      </Button>
                    </Space>
                  ),
                },
              ]}
            />
          </Card>

          {application?.status === 'draft' ? (
            <Card size="small" title={t('editDraft', { defaultValue: 'Edit draft application' })}>
              {applicationForm}
            </Card>
          ) : null}
        </Space>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Card size="small" title={t('enquiryName', { defaultValue: 'Enquiry' })}>
            <Form form={enquiryForm} layout="vertical" onFinish={(values) => enquiry.mutate(values)} style={{ maxWidth: 720 }}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="contact_name" label={t('enquiryName')} rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="phone" label={t('phone')}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="email" label={t('email')}>
                    <Input type="email" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="grade_interest" label={t('classApplied', { defaultValue: 'Class interest' })}>
                    <Input placeholder="e.g. Class I / Nursery" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="source_channel" label={t('sourceChannel', { defaultValue: 'Source' })}>
                    <Select allowClear options={SOURCES.map((v) => ({ value: v, label: v }))} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="preferred_session_label" label={t('academicYearLabel')}>
                    <Input placeholder="2026-27" />
                  </Form.Item>
                </Col>
              </Row>
              <Button htmlType="submit" loading={enquiry.isPending}>{t('save')}</Button>
            </Form>
          </Card>
          {noCampus ? (
            <Card size="small" title={t('createCampus', { defaultValue: 'Create campus' })}>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={t('campusRequiredTitle', { defaultValue: 'Create a campus first' })}
                description={t('campusEmptyHint', {
                  defaultValue: 'No campus yet — create one here, or under Students → New student',
                })}
              />
              <Form form={campusForm} layout="inline" onFinish={(values) => createCampus.mutate(values)}>
                <Form.Item name="code" rules={[{ required: true }]}>
                  <Input placeholder={t('campusCode')} />
                </Form.Item>
                <Form.Item name="name" rules={[{ required: true }]}>
                  <Input placeholder={t('campusName')} style={{ minWidth: 200 }} />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={createCampus.isPending}>
                  {t('createCampus', { defaultValue: 'Create campus' })}
                </Button>
              </Form>
            </Card>
          ) : null}
          <Card size="small" title={t('application')}>
            {applicationForm}
          </Card>
        </Space>
      )}
    </EducationPageChrome>
  );
}
